import React, { CSSProperties, FC, ReactNode } from 'react';

import { cn } from 'utils';

import styles from './MetricProgressBar.module.scss';

/**
 * Сегмент полосы — один проект группы.
 *
 * Полоса, разбитая на сегменты, показывает состав сделки: ширина сегмента это доля
 * проекта в общей сумме сбора, заливка внутри — сколько собрано по этому проекту.
 */
export type MetricProgressSegment = {
  key: string;
  /** Доля сегмента в полосе, в процентах. */
  share: number;
  /** Заливка внутри сегмента, в процентах от самого сегмента. */
  fill: number;
  /** Выделить сегмент — например, проект, страница которого открыта. */
  highlighted?: boolean;
  title?: string;
};

type Props = {
  currentValue: ReactNode;
  totalValue: ReactNode;
  currentLabel: string;
  totalLabel: string;
  /** Заливка цельной полосы. Игнорируется, если переданы segments. */
  percentage?: number;
  /** Разбивает полосу по проектам вместо одной усреднённой заливки. */
  segments?: Array<MetricProgressSegment>;
  className?: string;
};

const clamp = (value: number): number => Math.min(Math.max(value, 0), 100);

const MetricProgressBar: FC<Props> = ({
  currentValue,
  totalValue,
  currentLabel,
  totalLabel,
  percentage = 0,
  segments,
  className,
}) => {
  const renderTrack = () => {
    if (segments && segments.length > 0) {
      return (
        <div className={cn(styles.track, styles.trackSegmented)}>
          {segments.map((segment) => (
            <div
              className={cn(styles.segment, segment.highlighted && styles.segmentHighlighted)}
              style={
                {
                  '--segment-share': `${clamp(segment.share)}%`,
                  '--segment-fill': `${clamp(segment.fill)}%`,
                } as CSSProperties
              }
              key={segment.key}
              title={segment.title}
            >
              <div className={styles.segmentFill} />
            </div>
          ))}
        </div>
      );
    }

    return (
      <div
        className={styles.track}
        style={{ '--fill-width': `${clamp(percentage)}%` } as CSSProperties}
      >
        <div className={styles.fill} />
      </div>
    );
  };

  return (
    <div className={cn(styles.root, className)}>
      <div className={styles.values}>
        <span className={styles.value}>{currentValue}</span>
        <span className={cn(styles.value, styles.valueEnd)}>{totalValue}</span>
      </div>
      {renderTrack()}
      <div className={styles.labels}>
        <span className={styles.label}>{currentLabel}</span>
        <span className={cn(styles.label, styles.labelEnd)}>{totalLabel}</span>
      </div>
    </div>
  );
};

export default MetricProgressBar;
